"use client";

import { Dialog, Transition } from "@headlessui/react";
import { X } from "lucide-react";
import { Fragment, useState, useEffect } from "react";
import { FetchButton } from "./FetchButton";
import { FetchToggle } from "./FetchToggle";

interface FetchConfirmationDialogProps {
  /** Whether the dialog is open */
  isOpen: boolean;
  /** Called when the dialog is closed without confirming */
  onClose: () => void;
  /** Called when the user confirms the action */
  onConfirm: () => void | Promise<void>;
  /** Dialog title */
  title: string;
  /** Description content explaining the action. Accepts a plain string or rich block content (e.g. a bulleted list of reasons). */
  message: React.ReactNode;
  /** Text for the confirm button */
  confirmText?: string;
  /** Text for the cancel button */
  cancelText?: string;
  /**
   * Hide the outline cancel button, leaving only the primary action in the footer. Use when the
   * primary action IS the safe/dismiss choice (e.g. a "Cancel" confirm) so the footer isn't two
   * redundant dismiss buttons. The X and backdrop still dismiss via `onClose`.
   */
  hideCancel?: boolean;
  /** Visual style of the dialog */
  type?: "danger" | "warning" | "info";
  /**
   * Optional icon rendered to the left of the title. Caller supplies the
   * element (sized + colored as desired); a 40px circular tinted backdrop
   * wraps it for visual consistency.
   */
  icon?: React.ReactNode;
  /** Tailwind background color class for the icon backdrop (e.g. "bg-green-50"). Defaults based on `type`. */
  iconBgClass?: string;
  /**
   * Header layout. `"inline"` (default) sits the icon to the left of the title. `"stacked"` places
   * the icon above a left-aligned title + message column — better when the message is multi-line or
   * a list, where inline alignment leaves the body awkwardly indented beside the icon.
   */
  layout?: "inline" | "stacked";
  /**
   * Which button carries the primary (filled) emphasis. Defaults to `"confirm"`.
   * Set to `"cancel"` when the safer choice is to back out — the cancel button
   * becomes the filled CTA and the confirm button becomes the muted outline.
   */
  emphasis?: "confirm" | "cancel";
  /**
   * When `emphasis="cancel"`, controls the filled cancel button's color. Defaults to
   * `"primary"` (blue "back out safely" CTA). Set `"semantic"` to use the dialog's
   * `type` color instead (e.g. red for `type="danger"`) when the emphasized action
   * should still visually carry the danger/warning context it's backing out of.
   */
  emphasizedCancelColor?: "primary" | "semantic";
  /** Whether the confirm action is in progress */
  isLoading?: boolean;
  /** Whether to require a toggle confirmation before the confirm button is enabled */
  requiresToggle?: boolean;
  /** Tailwind z-index class applied to the dialog root. Override when rendering above a drawer or other elevated surface. */
  zIndexClass?: string;
}

const BUTTON_COLOR_MAP = {
  danger: "destructive" as const,
  warning: "warning" as const,
  info: "primary" as const,
};

const ICON_BG_BY_TYPE: Record<NonNullable<FetchConfirmationDialogProps["type"]>, string> = {
  danger: "bg-red-50",
  warning: "bg-yellow-50",
  info: "bg-indigo-50",
};

/** Confirmation dialog for destructive or important actions */
export const FetchConfirmationDialog = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = "Confirm",
  cancelText = "Cancel",
  hideCancel = false,
  type = "danger",
  icon,
  iconBgClass,
  layout = "inline",
  emphasis = "confirm",
  emphasizedCancelColor = "primary",
  isLoading = false,
  requiresToggle = false,
  zIndexClass = "z-50",
}: FetchConfirmationDialogProps) => {
  // States
  const [toggleChecked, setToggleChecked] = useState(false);

  // Effects — reset toggle when dialog closes
  useEffect(() => {
    if (!isOpen) setToggleChecked(false);
  }, [isOpen]);

  const buttonColor = BUTTON_COLOR_MAP[type];
  const isConfirmDisabled = isLoading || (requiresToggle && !toggleChecked);
  const isCancelEmphasized = emphasis === "cancel";

  const confirmButton = (
    <FetchButton
      variant={isCancelEmphasized ? "outline" : "primary"}
      color={isCancelEmphasized ? "neutral" : buttonColor}
      size="sm"
      onClick={async () => {
        await onConfirm();
        onClose();
      }}
      disabled={isConfirmDisabled}
      loading={isLoading}
    >
      {confirmText}
    </FetchButton>
  );

  const cancelButton = (
    <FetchButton
      variant={isCancelEmphasized ? "primary" : "outline"}
      color={isCancelEmphasized ? (emphasizedCancelColor === "semantic" ? buttonColor : "primary") : "neutral"}
      size="sm"
      onClick={onClose}
      disabled={isLoading}
    >
      {cancelText}
    </FetchButton>
  );

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className={`relative ${zIndexClass}`} onClose={isLoading ? () => {} : onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white shadow-xl transition-all">
                <div className="p-6">
                  {layout === "stacked" ? (
                    <div className="relative">
                      <button
                        type="button"
                        onClick={onClose}
                        disabled={isLoading}
                        aria-label="Close"
                        className="absolute right-0 top-0 rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-500"
                      >
                        <X className="h-5 w-5" />
                      </button>
                      <div className="flex items-center gap-3 pr-8">
                        {icon && (
                          <div
                            className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full ${iconBgClass ?? ICON_BG_BY_TYPE[type]}`}
                          >
                            {icon}
                          </div>
                        )}
                        <Dialog.Title as="h3" className="text-lg font-semibold text-gray-900">
                          {title}
                        </Dialog.Title>
                      </div>
                      <Dialog.Description as="div" className="mt-3 text-sm font-medium text-gray-500">
                        {message}
                      </Dialog.Description>
                    </div>
                  ) : (
                    <div className="flex items-start justify-between gap-3">
                      {icon && (
                        <div
                          className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full ${iconBgClass ?? ICON_BG_BY_TYPE[type]}`}
                        >
                          {icon}
                        </div>
                      )}
                      <div className="flex-1">
                        <Dialog.Title as="h3" className="text-lg font-semibold text-gray-900">
                          {title}
                        </Dialog.Title>
                        <Dialog.Description as="div" className="mt-1 text-sm font-medium text-gray-500">
                          {message}
                        </Dialog.Description>
                      </div>
                      <button
                        type="button"
                        onClick={onClose}
                        disabled={isLoading}
                        aria-label="Close"
                        className="flex-shrink-0 rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-500"
                      >
                        <X className="h-5 w-5" />
                      </button>
                    </div>
                  )}

                  {requiresToggle && (
                    <div className="mt-5">
                      <FetchToggle
                        checked={toggleChecked}
                        onChange={setToggleChecked}
                        size="sm"
                        color="destructive"
                        label="I understand this action cannot be undone"
                      />
                    </div>
                  )}
                </div>

                {/* Emphasized (filled) button sits on the right. */}
                <div className="flex justify-end gap-3 px-6 pb-6">
                  {isCancelEmphasized ? (
                    <>
                      {confirmButton}
                      {!hideCancel && cancelButton}
                    </>
                  ) : (
                    <>
                      {!hideCancel && cancelButton}
                      {confirmButton}
                    </>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};
